import { useCallback, useEffect, useState } from 'react';
import {
  getDataIndexedColumns,
  getDefaultTablePagination,
  useTableSearchParams,
} from 'shared/UI/UIKit/Table';
import { useFilteredColumns } from 'common/ColumnsFilter/use-filtered-columns';
import { getBuildingList, type Building } from 'services/api';
import type { AppTableColumn, AppTableState } from 'shared/UI/UIKit/Table';
import type { BuildingListRequest } from 'services/api';
import { getBuidingsTableColumns } from './columns';

const getInitialStateWithSorters = (columns: AppTableColumn<Building>[]): AppTableState => {
  return {
    sorters: getDataIndexedColumns(columns)
      .filter(({ key }) => !['controls', 'count'].includes(key))
      .map(({ key, dataIndex }) => {
        return key === 'id'
          ? {
              key,
              dataIndex,
              order: 'descend',
            }
          : {
              key,
              dataIndex,
            };
      }),
    filters: [],
    pagination: getDefaultTablePagination(),
  };
};

export const useBuildings = () => {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [siderVisible, setSiderVisible] = useState(false);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);

  const editBuilding = (id: number) => {
    const foundBuilding = buildings.find((building) => building.id === id);

    if (foundBuilding) {
      setSelectedBuilding(foundBuilding);
      setEditModalVisible(true);
    }
  };

  const removeBuilding = (id: number) => {
    const foundBuilding = buildings.find((building) => building.id === id);

    if (foundBuilding) {
      setSelectedBuilding(foundBuilding);
      setRemoveModalVisible(true);
    }
  };

  const columns = getBuidingsTableColumns(editBuilding, removeBuilding);
  const { filteredColumns, hiddenColumns, visibleKeys, setVisibleKeys } = useFilteredColumns(
    columns,
    ['id'],
  );

  const getTableItems = (nextTableState: AppTableState, nextFilterState: Record<string, any>) => {
    const {
      pagination: { current, pageSize },
      sorters,
    } = nextTableState;
    const sort = sorters
      .filter((col) => !!col.order)
      .map((col) => `${col.dataIndex.join('.')},${col.order === 'ascend' ? 'asc' : 'desc'}`);

    // Адаптируем параметры для API зданий (page/size вместо page/limit)
    const apiParams: BuildingListRequest = {
      page: current! - 1,
      size: pageSize,
      sort: sort.length > 0 ? sort : undefined,
      ...nextFilterState,
    };

    setLoading(true);

    getBuildingList(apiParams)
      .then((res) => {
        setBuildings(res.content);
        setTotal(res.totalElements);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const {
    tableState,
    onTableChange,
    filterState,
    filterCount,
    onFilterChange,
    onItemRemove,
    refreshPage,
  } = useTableSearchParams(
    () => getInitialStateWithSorters(columns),
    getTableItems,
    buildings.length,
    total,
    hiddenColumns,
  );

  useEffect(() => {
    return () => {
      setBuildings([]);
      setTotal(0);
      setLoading(false);
      setSelectedBuilding(null);
    };
  }, []);

  const createBuilding = () => {
    setCreateModalVisible(true);
  };

  const showSider = () => {
    setSiderVisible(true);
  };
  const hideSider = useCallback(() => {
    setSiderVisible(false);
  }, []);

  const hideEditModal = () => {
    setEditModalVisible(false);
    setSelectedBuilding(null);
  };

  return {
    state: {
      loading,
      buildings,
      selectedBuilding,
      removeModalVisible,
      createModalVisible,
      editModalVisible,
      tableState,
      siderVisible,
      columns,
      filteredColumns,
      visibleKeys,
      filterState,
      filterCount,
    },
    methods: {
      setRemoveModalVisible,
      createBuilding,
      editBuilding,
      removeBuilding,
      setCreateModalVisible,
      onTableChange,
      showSider,
      hideSider,
      setVisibleKeys,
      onFilterChange,
      onItemRemove,
      refreshPage,
      hideEditModal,
    },
  };
};
